import {
  Table as RawTable,
  withI18n,
  PaginationState,
  OnChangeFn,
  type ITableProps,
  SortingState,
  PageLayout,
  TableFilters as RawTableFilters,
  FilterType,
  type ITableFiltersProps,
} from '@psyomics/ui-components';
import { useTranslation } from 'react-i18next';

import { ReferralSessionInvites } from './referral-session-invites';
import { GlobalSearch, ReferralSessionResult } from './types';
import { useTableColumns } from './use-table-columns';

const TableWithI18n = withI18n<ITableProps<ReferralSessionResult>>(RawTable);
const TableFilters = withI18n<ITableFiltersProps>(RawTableFilters);

const Table = <TData,>(
  props: Parameters<typeof TableWithI18n>[0] & {
    data: TData[];
  }
) => TableWithI18n(props);

type ReferralsViewProps = {
  readonly referrals: ReferralSessionResult[];
  readonly pagination: PaginationState;
  readonly sorting: SortingState;
  readonly totalCount: number;
  readonly onPaginationChange: OnChangeFn<PaginationState>;
  readonly onSortingChange: OnChangeFn<SortingState>;
  readonly filters: FilterType[];
  readonly globalSearch: GlobalSearch;
  readonly isSummary: boolean;
};

const ReferralsView = ({
  globalSearch: globalFilter,
  referrals,
  totalCount,
  pagination: { pageIndex, pageSize },
  sorting,
  onPaginationChange,
  onSortingChange,
  filters,
  isSummary,
}: ReferralsViewProps) => {
  const { i18n, t } = useTranslation();
  const columns = useTableColumns();

  return (
    <PageLayout background="muted" title={isSummary ? undefined : t('page.referrals.title')} width="wide">
      {!isSummary && (
        <TableFilters className="mb-4" filters={filters} globalFilter={globalFilter} i18nInstance={i18n} />
      )}
      <Table
        columns={columns}
        data={referrals}
        i18nInstance={i18n}
        manipulationOptions={{
          currentPage: pageIndex,
          onPaginationChange,
          onSortingChange,
          sorting,
          totalRowCount: totalCount,
          type: 'serverSide',
        }}
        pageSize={pageSize}
        renderSubRow={isSummary ? undefined : (session) => <ReferralSessionInvites session={session} />}
      />
    </PageLayout>
  );
};

export { ReferralsView };
